import { IUser } from "./../Auth"
import { ITag } from "../Tags"
import { IListResponse, XHRInstance } from "@sembark-travel/xhr"
import { TTeam } from "../Teams"
import { useState, useCallback } from "react"
import { useXHR } from "@sembark-travel/xhr"
import useSWR from "swr"
import { queryToSearch } from "@sembark-travel/ui/router"
import { useNavigate } from "../router"
import { ITripSource } from "../TripSources"
import { TTripDestination } from "../TripDestinations"
import { IComment } from "../Comments"

export interface ITripPlanRequest {
	id: number
	name: string
	phone_number: string
	email?: string
	destination?: string
	start_date?: string
	no_of_days?: number
	no_of_adults?: number
	no_of_children?: number
	hotel_preference?: string
	comments?: string
	trip_id?: number
	owner_id?: number
	owner?: IUser
	assigned_at?: string
	created_at: string
	reference_id?: string
	trip_source: string
	tags?: Array<ITag>
	notes?: IComment[]
	extra_fields?: Record<string, unknown>
	deleted_at?: string
	deleted_by?: IUser
}

type TParameterType = "string" | "integer"

export interface TParameter {
	name: string
	display_name?: string
	operator?: TOperator
	attribute: string
	type: TParameterType
	values?: string[] // optional, since some parameters don't have values
}

type TRightOperandType =
	| "integer"
	| "array<integer>"
	| "string"
	| "array<string>"

export interface TOperator {
	name: string
	right_operand_type: TRightOperandType
}

export interface TOperatorGroup {
	left_operand_type: "integer" | "string" | "any"
	operators: TOperator[]
}

export interface TRuleConfig {
	data: {
		parameters: TParameter[]
		operators: TOperatorGroup[]
	}
}

export interface TAnalysisParameters {
	data: { display_name: string; name: string; values?: string[] }[]
}

export interface TAppliedRules {
	data: {
		grouped_rule_items: {
			right_operand_value: string
			group_id: number
			id: number
			parameter: TParameter & { operator: TOperator }
			description: string
		}[][]
		id: number
		teams: TTeam[]
		users: IUser[]
		name: string
		priority: number
	}[]
}
export function AutoDistributionXHR(xhr: XHRInstance) {
	return {
		async getRoutingRules(params?: unknown): Promise<TRuleConfig> {
			return xhr
				.get("/routing-rules/create", { params })
				.then((resp) => resp.data)
		},
		async getAppliedRules(params?: unknown): Promise<TAppliedRules> {
			return xhr.get("/routing-rules", { params }).then((resp) => resp.data)
		},
		async getAnalysisParameters(
			params?: unknown
		): Promise<TAnalysisParameters> {
			return xhr
				.get("/routing-rules-analysis-parameters", { params })
				.then((resp) => resp.data)
		},
	}
}

function reorder<T>(list: T[], from: number, to: number): T[] {
	const result = [...list]
	const [moved] = result.splice(from, 1)
	result.splice(to, 0, moved)
	return result
}

export function useDragDrop<T>(items: T[], onReorder: (items: T[]) => void) {
	const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)
	const [dragOverPosition, setDragOverIndexPosition] = useState<
		"top" | "bottom" | null
	>(null)

	const handleDragStart = useCallback(
		(e: React.DragEvent<HTMLElement>, index: number) => {
			e.dataTransfer.setData("dragIndex", index.toString())
			e.dataTransfer.effectAllowed = "move"
		},
		[]
	)

	const handleDragOver = useCallback(
		(e: React.DragEvent<HTMLElement>, index: number) => {
			e.preventDefault()
			const bounding = (e.currentTarget as HTMLElement).getBoundingClientRect()
			const offset = e.clientY - bounding.top
			const position = offset < bounding.height / 2 ? "top" : "bottom"
			setDragOverIndex(index)
			setDragOverIndexPosition(position)
		},
		[]
	)

	const handleDrop = useCallback(
		(e: React.DragEvent<HTMLElement>, dropIndex: number) => {
			const dragIndexStr = e.dataTransfer.getData("dragIndex")
			if (!dragIndexStr) return

			const dragIndex = Number(dragIndexStr)
			if (isNaN(dragIndex)) return

			let targetIndex = dropIndex
			if (dragOverPosition === "bottom") {
				targetIndex = dropIndex + 1
			}

			const adjustedIndex =
				dragIndex < targetIndex ? targetIndex - 1 : targetIndex
			const reordered = reorder(items, dragIndex, adjustedIndex)

			onReorder(reordered)
			setDragOverIndex(null)
			setDragOverIndexPosition(null)
		},
		[items, dragOverPosition, onReorder]
	)

	const getCellStyle = useCallback(
		(rowIndex: number) => {
			if (dragOverIndex !== rowIndex) {
				return {}
			}

			const isTop = dragOverPosition === "top"
			return {
				boxShadow: isTop ? "0 3px 0 blue inset" : "0 -3px 0 blue inset",
				backgroundColor: "rgba(0, 0, 255, 0.05)",
				transition: "background-color 0.2s, boxShadow 0.2s",
				zIndex: 1,
				cursor: "grab",
			}
		},
		[dragOverIndex, dragOverPosition]
	)
	const handleDragLeave = useCallback(() => {
		setDragOverIndex(null)
		setDragOverIndexPosition(null)
	}, [])

	return {
		handleDragStart,
		handleDragOver,
		handleDrop,
		getRowStyle: getCellStyle,
		handleDragLeave,
	}
}

export const isMouseEvent =
	typeof window !== "undefined" && typeof window.matchMedia === "function"
		? window.matchMedia("(any-pointer: fine)").matches
		: false
export type TTripPlanRequestReportStats = {
	archived: number
	moved_to_trips: number
	pending_followups: number
	total: number
	active_followups: number
}

export function useTripPlanRequestReportStats({
	params,
}: {
	params: Record<string, unknown>
}) {
	const xhr = useXHR()
	const { page, ...otherParams } = params
	const { data: stats, error } = useSWR(
		`/trip-plan-request-report/stats?${queryToSearch(otherParams)}`,
		() =>
			xhr
				.get<{ data: TTripPlanRequestReportStats }>(
					"/trip-plan-request-report/stats",
					{
						params: {
							...params,
						},
					}
				)
				.then((resp) => resp.data.data)
	)
	return { stats, error }
}
export const LeadStatusType = [
	{ name: "Moved to Trips", id: "moved" },
	{ name: "Active Followup", id: "active_followups" },
	{ name: "Pending Followup", id: "pending_followups" },
	{ name: "Archived", id: "archived" },
]

export function useTripPlanRequestRedirect<T>() {
	const navigate = useNavigate()

	const onRedirect = useCallback(
		(query: T) => {
			if (query) {
				navigate({
					pathname: "/trip-plan-requests",
					search: queryToSearch(query),
				})
			}
		},
		[navigate]
	)

	return { onRedirect }
}

export function TripPlanRequestXHR(xhr: XHRInstance) {
	return {
		async getTripPlanRequests(
			params?: unknown
		): Promise<IListResponse<ITripPlanRequest>> {
			return xhr
				.get("/trip-plan-requests", { params })
				.then((resp) => resp.data)
		},
		async getTripPlanRequestSources(): Promise<
			Array<{ id: string; name: string }>
		> {
			return xhr
				.get("/trip-plan-request-sources")
				.then((resp) => resp.data.data)
		},
		async archiveTripPlanRequest(
			id: string | number
		): Promise<{ message: string }> {
			return xhr.delete(`/trip-plan-requests/${id}`).then((resp) => resp.data)
		},
		async restoreTripPlanRequest(
			id: string | number
		): Promise<{ message: string }> {
			return xhr
				.patch(`/trip-plan-requests/${id}/restore`)
				.then((resp) => resp.data)
		},
		async assignTripRequestsOwner(
			requestIds: Array<number>,
			ownerId?: number | null
		): Promise<IListResponse<ITripPlanRequest>> {
			return xhr
				.post("/trip-plan-request-owners", {
					items: requestIds,
					owner_id: ownerId,
				})
				.then((resp) => resp.data)
		},
		async getPayloadForTrip(requestId: number | string) {
			return xhr
				.get<{
					data: {
						trip_source: ITripSource
						reference_id: string | null
						destinations: Array<TTripDestination> | null
						start_date: string | null
						no_of_days: number
						no_of_nights: number
						no_of_adults: number
						children: Array<{ age: number; count: number }> | null
						tourist: {
							name: string
							email: string | null
							phone_numbers: Array<string> | null
						}
						comments: string | null
					}
				}>(`/trip-plan-requests/${requestId}/trip/create`)
				.then((resp) => resp.data.data)
		},
		async attachTrip(
			requestId: number | string,
			data: unknown
		): Promise<ITripPlanRequest> {
			return xhr
				.post(`/trip-plan-requests/${requestId}/trip`, data)
				.then((resp) => resp.data)
		},

		async getTripPlanRequestDestinations(
			params?: unknown
		): Promise<Array<{ id: string; name: string }>> {
			return xhr
				.get("/trip-plan-request-destinations", { params })
				.then((resp) => resp.data.data)
		},

		async editTagsAndFollowUps(
			requestId: number | string,
			{
				tags,
				note,
				due_at,
			}: {
				tags?: Array<string>
				note?: string
				due_at?: string
			}
		): Promise<ITripPlanRequest> {
			return xhr
				.post(`/trip-plan-requests/${requestId}/tags-and-followups`, {
					tags,
					note,
					due_at,
				})
				.then((resp) => resp.data)
		},
	}
}
